import _ from 'lodash'

/**
 * Estado de controle e decisão de submissão de resposta — puro, sem React.
 *
 * `resolveAnswerSubmission` concentra o miolo do antigo `onAnswer`: é ele que
 * decide se um coração é perdido e se a resposta chega a ir para a API. Essa
 * decisão tinha duas cópias (`useLevel.ts` e `quizzes/Show/context.js`) que
 * podiam divergir sem que nada quebrasse visivelmente.
 */

export interface ControlStateInput {
  answer: unknown
  isSubmitting: boolean
  answerIsWrong: boolean
  answerIsRight: boolean | undefined
}

export interface ControlState {
  answerIsEmpty: boolean
  disableInputs: boolean
  disableControls: boolean
}

/**
 * Deriva se a resposta está vazia e o que isso desabilita.
 *
 * O critério de vazio é `!answer || _.isEmpty(answer)`, exatamente como jornada
 * (`useLevel.ts`) e quiz (`context.js`) já faziam. Isso trata `0` e `false`
 * como vazios — herdado e preservado de propósito: mudar aqui alteraria em
 * quais kinds o CTA destrava.
 */
export const deriveControlState = ({
  answer,
  isSubmitting,
  answerIsWrong,
  answerIsRight
}: ControlStateInput): ControlState => {
  const answerIsEmpty = !answer || _.isEmpty(answer)
  const disableInputs = isSubmitting || answerIsWrong || !!answerIsRight

  return {
    answerIsEmpty,
    disableInputs,
    disableControls: disableInputs || answerIsEmpty
  }
}

export interface AnswerSubmissionInput {
  isSubmitting: boolean
  answerIsCorrect: boolean
  /** Avanço sem verificação — slides e o modo de interação "continue". */
  skipValidation: boolean
  /** `allow_wrong_answers` do recurso. */
  allowWrongAnswers: boolean
}

export type AnswerSound = 'correct' | 'wrong' | null

export type AnswerSubmissionResolution =
  /** Já há submissão em andamento — a chamada é engolida. */
  | { kind: 'ignored' }
  /** Erra e o recurso não permite avançar: dá feedback, mas não chama a API. */
  | {
      kind: 'blocked'
      answerIsRight: boolean
      markWrong: boolean
      loseHeart: boolean
      sound: AnswerSound
    }
  /** Segue para a API. */
  | {
      kind: 'submit'
      answerIsRight: boolean
      markWrong: boolean
      loseHeart: boolean
      sound: AnswerSound
    }

/**
 * Decide o que acontece quando o participante responde.
 *
 * Regras, na ordem em que o `onAnswer` da jornada as aplicava:
 *
 * - submissão em andamento engole a chamada;
 * - a resposta conta como certa quando está correta **ou** quando a validação
 *   foi pulada;
 * - som só toca quando há validação;
 * - errar marca o erro; o coração só cai se o recurso não permitir respostas
 *   erradas;
 * - errar sem permissão de avançar **interrompe** — a resposta não vai à API.
 */
export const resolveAnswerSubmission = ({
  isSubmitting,
  answerIsCorrect,
  skipValidation,
  allowWrongAnswers
}: AnswerSubmissionInput): AnswerSubmissionResolution => {
  if (isSubmitting) return { kind: 'ignored' }

  const answerIsRight = answerIsCorrect || skipValidation
  const markWrong = !answerIsCorrect && !skipValidation
  const loseHeart = markWrong && !allowWrongAnswers
  const sound: AnswerSound = skipValidation
    ? null
    : answerIsCorrect
      ? 'correct'
      : 'wrong'

  const outcome = { answerIsRight, markWrong, loseHeart, sound }

  if (markWrong && !allowWrongAnswers) return { kind: 'blocked', ...outcome }

  return { kind: 'submit', ...outcome }
}
